/**
 * Analytics Service - Tracks user interactions with time-saved estimates
 * 
 * Time saved values are based on reasonable estimates:
 * - Research/learning time avoided
 * - Manual work replaced by automation
 * - Knowledge sharing efficiency gains
 */

import { supabase } from '../utils/supabaseClient';

// Time saved in minutes for each event type
// Conservative estimates based on actual implementation value
export const TIME_SAVED_MINUTES: Record<string, number> = {
  // Module interactions - viewing doesn't save time, implementation does
  module_view: 0,        // Viewing alone doesn't save time - implementation does
  module_copy: 10,       // Copying content saves some manual typing
  module_like: 0,        // Liking doesn't save time
  module_create: 0,      // Creating helps others, tracked via implementations
  module_implement: 0,   // Uses module-specific time (set dynamically)
  
  // How-To interactions
  howto_view: 0,         // Reading alone doesn't save time
  howto_copy: 10,        // Copying content saves manual typing
  howto_create: 0,       // Creating helps others, tracked via views
  
  // Agent interactions - these DO save time
  agent_run: 15,         // Each agent run replaces manual task
  agent_create: 0,       // Creating saves time via runs, not creation
  
  // Search interactions
  search: 0,             // Searching doesn't save time directly
};

// Time saved by module type (in minutes PER WEEK) when implemented
// Moderate estimates - realistic weekly productivity gains
export const TIME_SAVED_BY_MODULE_TYPE: Record<string, number> = {
  // AUTOMATION - Replaces recurring manual work (most valuable)
  'automation': 180,          // Automations save ~3 hrs/week of manual tasks
  'workflow': 150,            // Workflow saves ~2.5 hrs/week
  'agent': 180,               // AI Agents save ~3 hrs/week
  'bot': 150,                 // Bots automate tasks - 2.5 hrs/week
  'script': 120,              // Scripts save ~2 hrs/week on recurring tasks
  
  // AI TOOLS - Accelerates work
  'custom_gpt': 120,          // Custom GPT saves ~2 hrs/week on repeated queries
  'gpt': 90,                  // GPT configs save ~1.5 hrs/week
  'ai_tool': 90,              // AI tools save ~1.5 hrs/week
  
  // INTEGRATIONS - Reduces manual data entry
  'integration': 120,         // Integrations save ~2 hrs/week of manual syncing
  'api': 90,                  // API connections save ~1.5 hrs/week
  'connector': 90,            // Connectors save ~1.5 hrs/week
  
  // PRODUCTS/TOOLS - Ready-to-use solutions
  'product': 150,             // Products save ~2.5 hrs/week
  'prototype': 120,           // Prototypes save ~2 hrs/week
  'tool': 90,                 // Tools save ~1.5 hrs/week
  'dashboard': 60,            // Dashboards save ~1 hr/week on reporting
  
  // TEMPLATES - Speeds up content creation
  'template': 60,             // Templates save ~1 hr/week
  'prompt_template': 45,      // Prompt templates save ~45 min/week
  'prompt': 30,               // Ready prompts save ~30 min per use
  'framework': 90,            // Frameworks save ~1.5 hrs/week
  'report': 60,               // Report templates save ~1 hr/week
  
  // GUIDES/LEARNING - One-time benefit (less ongoing value)
  'guide': 30,                // Guides save ~30 min of research
  'tutorial': 30,             // Tutorials save ~30 min learning
  'case_study': 20,           // Case studies save ~20 min of research
  'checklist': 30,            // Checklists save ~30 min ensuring completeness
  'documentation': 20,        // Docs save ~20 min finding answers
};

// Tag-based time bonuses (minutes/week) - moderate additions
export const TIME_BONUS_BY_TAG: Record<string, number> = {
  // AUTOMATION tags - adds value when module automates tasks
  'automation': 45,
  'automated': 45,
  'workflow': 30,
  'scheduled': 40,
  'recurring': 40,
  
  // NO-CODE/LOW-CODE - easier to implement
  'no-code': 35,
  'low-code': 25,
  'zapier': 35,
  'make': 35,
  'n8n': 35,
  
  // AI - accelerates work
  'ai': 25,
  'chatgpt': 25,
  'gpt-4': 35,
  'gpt': 25,
  'claude': 35,
  'llm': 25,
  'custom gpt': 45,
  'ai agent': 50,
  
  // INTEGRATION - connects systems
  'api': 35,
  'integration': 40,
  'webhook': 25,
  'sync': 30,
  
  // SCALE - works on multiple items
  'scalable': 25,
  'bulk': 35,
  'batch': 30,
  'mass': 25,
  
  // MARKETING TASKS
  'seo': 30,
  'content marketing': 20,
  'email marketing': 35,
  'social media': 20,
  'analytics': 30,
  'reporting': 35,
  'paid media': 35,
  'ppc': 30,
  'campaign': 25,
  
  // PRODUCT/TOOLS
  'product': 40,
  'prototype': 35,
  'mvp': 35,
  'tool': 25,
  'dashboard': 30,
  
  // TEMPLATE/REUSABLE
  'template': 20,
  'reusable': 25,
  'framework': 35,
  
  // COMPLEXITY
  'advanced': 20,
  'enterprise': 35,
  'end-to-end': 30,
  'comprehensive': 20,
};

/**
 * Calculate time saved for a module based on type and tags
 * Returns time in minutes - moderate estimate of weekly time savings when implemented
 */
export function calculateModuleTimeSaved(moduleType: string, tags: string[]): number {
  // Base time from module type
  const normalizedType = moduleType?.toLowerCase().replace(/[_-]/g, '') || '';
  let basetime = TIME_SAVED_BY_MODULE_TYPE[normalizedType] || 
                 TIME_SAVED_BY_MODULE_TYPE[moduleType?.toLowerCase()] || 
                 30; // Default: 30 min saved
  
  // Add bonuses from tags
  let tagBonus = 0;
  const normalizedTags = (tags || []).map(t => t.toLowerCase());
  
  Object.entries(TIME_BONUS_BY_TAG).forEach(([tag, bonus]) => {
    if (normalizedTags.some(t => t.includes(tag.toLowerCase()) || tag.toLowerCase().includes(t))) {
      tagBonus += bonus;
    }
  });
  
  // Cap tag bonus at 75% of base time
  tagBonus = Math.min(tagBonus, basetime * 0.75);
  
  return Math.round(basetime + tagBonus);
}

/**
 * Keywords that indicate the module provides AUTOMATION value
 * Moderate values - these add meaningful bonuses
 */
const AUTOMATION_INDICATORS: Record<string, number> = {
  // Direct automation
  'automate': 30, 'automated': 30, 'automation': 30,
  'auto-': 15, 'automatically': 25,
  'schedule': 25, 'scheduled': 25, 'recurring': 30,
  'trigger': 20, 'webhook': 25, 'cron': 25,
  'workflow': 25, 'pipeline': 25,
  
  // Automation tools
  'zapier': 25, 'make': 25, 'n8n': 25, 'integromat': 20,
  'power automate': 25, 'ifttt': 15,
  
  // Batch/bulk processing
  'bulk': 25, 'batch': 25, 'mass': 20, 'multiple': 10,
};

/**
 * Keywords indicating AI-powered productivity gains
 */
const AI_PRODUCTIVITY_INDICATORS: Record<string, number> = {
  // AI assistants
  'custom gpt': 40, 'gpt': 25, 'chatgpt': 25, 'gpt-4': 35,
  'claude': 30, 'ai assistant': 35, 'ai agent': 45,
  'copilot': 30, 'assistant': 15,
  
  // AI for content generation
  'generate': 20, 'generator': 20, 'generation': 20,
  'write': 10, 'writing': 10, 'draft': 10,
  
  // AI for analysis
  'analyze': 20, 'analysis': 20, 'summarize': 20, 'summary': 15,
  'research': 20, 'insights': 10,
};

/**
 * Keywords indicating template/reusable value
 */
const TEMPLATE_INDICATORS: Record<string, number> = {
  'template': 20, 'templates': 20, 'reusable': 20,
  'plug and play': 25, 'ready-to-use': 25, 'copy and paste': 15,
  'framework': 25, 'boilerplate': 20, 'starter': 15,
  'blueprint': 25, 'playbook': 25,
};

/**
 * Keywords indicating integration/sync value
 */
const INTEGRATION_INDICATORS: Record<string, number> = {
  'integration': 25, 'integrate': 20, 'connect': 10,
  'sync': 25, 'syncing': 25, 'synchronize': 25,
  'import': 10, 'export': 10, 'transfer': 10,
  'api': 20, 'webhook': 25,
};

/**
 * Task frequency multipliers - how often the task is performed
 */
const FREQUENCY_INDICATORS: Record<string, number> = {
  'daily': 1.5, 'every day': 1.5,
  'weekly': 1.3, 'every week': 1.3,
  'hourly': 2.0, 'every hour': 2.0,
  'constant': 1.7, 'continuous': 1.7, 'ongoing': 1.3,
  'repetitive': 1.5, 'repeat': 1.3,
};

/**
 * Analyze module content to estimate time savings FROM IMPLEMENTATION
 * This measures how much time a user saves per week by using this module in their work
 * Conservative estimates - what someone actually saves by implementing this
 * 
 * @returns Estimated time saved in minutes per week when implemented
 */
export function analyzeModuleTimeSaved(
  moduleType: string,
  tags: string[],
  title: string,
  description: string,
  setupInstructions?: string
): number {
  // Start with base time from module type + tags
  let baseTime = calculateModuleTimeSaved(moduleType, tags);
  
  // Combine content for analysis
  const content = `${title} ${description} ${setupInstructions || ''}`.toLowerCase();
  
  let contentBonus = 0;
  let frequencyMultiplier = 1.0;
  
  // Check for automation indicators (modest bonus)
  Object.entries(AUTOMATION_INDICATORS).forEach(([keyword, value]) => {
    if (content.includes(keyword.toLowerCase())) {
      contentBonus += value;
    }
  });
  
  // Check for AI productivity indicators
  Object.entries(AI_PRODUCTIVITY_INDICATORS).forEach(([keyword, value]) => {
    if (content.includes(keyword.toLowerCase())) {
      contentBonus += value;
    }
  });
  
  // Check for template indicators
  Object.entries(TEMPLATE_INDICATORS).forEach(([keyword, value]) => {
    if (content.includes(keyword.toLowerCase())) {
      contentBonus += value;
    }
  });
  
  // Check for integration indicators
  Object.entries(INTEGRATION_INDICATORS).forEach(([keyword, value]) => {
    if (content.includes(keyword.toLowerCase())) {
      contentBonus += value;
    }
  });
  
  // Apply frequency multiplier (daily tasks save more than weekly)
  Object.entries(FREQUENCY_INDICATORS).forEach(([keyword, multiplier]) => {
    if (content.includes(keyword.toLowerCase())) {
      frequencyMultiplier = Math.max(frequencyMultiplier, multiplier);
    }
  });
  
  // Specific use case bonuses (moderate)
  const useCaseBonuses: Array<[RegExp, number]> = [
    [/email|inbox|gmail|outlook/i, 20],
    [/report|reporting|dashboard/i, 25],
    [/social media|post|posting/i, 15],
    [/data entry|spreadsheet|crm/i, 25],
    [/meeting|calendar|schedule/i, 20],
    [/client|customer|outreach/i, 15],
    [/seo|keyword|ranking/i, 20],
    [/ad|ads|campaign|ppc/i, 25],
  ];
  
  useCaseBonuses.forEach(([pattern, bonus]) => {
    if (pattern.test(content)) {
      contentBonus += bonus;
    }
  });
  
  // Cap content bonus at 75% of base time
  contentBonus = Math.min(contentBonus, baseTime * 0.75);
  
  // Calculate final time saved with frequency adjustment
  let totalMinutes = (baseTime + contentBonus) * frequencyMultiplier;
  
  // Cap at 5 hours/week max (300 min) - reasonable maximum for most modules
  totalMinutes = Math.min(totalMinutes, 300);
  
  // Minimum 15 minutes
  totalMinutes = Math.max(totalMinutes, 15);
  
  return Math.round(totalMinutes);
}

/**
 * Format time saved as human-readable string (hours or minutes)
 */
export function formatTimeSaved(minutes: number): string {
  if (minutes >= 60) {
    const hours = minutes / 60;
    if (hours >= 10) {
      return `${Math.round(hours)}+ hrs`;
    }
    return hours % 1 === 0 ? `${hours} hrs` : `${hours.toFixed(1)} hrs`;
  }
  return `${minutes} min`;
}

// Hourly rate for dollar calculations
export const HOURLY_RATE = 150;

/**
 * AI-powered time savings analysis
 * Uses Claude to analyze module content and estimate realistic time savings
 * Returns both the estimate (in minutes) and a reasoning explanation
 */
export async function analyzeTimeSavingsWithAI(
  title: string,
  description: string,
  moduleType: string,
  tags: string[],
  setupInstructions?: string
): Promise<{ estimate: number; reasoning: string }> {
  try {
    const prompt = `Analyze this module and estimate how much time (in minutes per week) a marketing professional would save by implementing it. Be realistic and conservative.

MODULE DETAILS:
- Title: ${title}
- Type: ${moduleType || 'general'}
- Tags: ${tags?.join(', ') || 'none'}
- Description: ${description}
${setupInstructions ? `- Setup Instructions: ${setupInstructions.substring(0, 500)}...` : ''}

GUIDELINES:
- Prompts/templates typically save 15-45 minutes/week
- Automations that replace manual tasks save 1-3 hours/week
- Custom GPTs/AI agents that handle repeated queries save 1-2 hours/week
- Integrations that sync data automatically save 30-90 minutes/week
- One-time guides/tutorials provide ~30 min of learning value (not ongoing)
- Be realistic - most modules save 30 min to 2 hours per week

Respond in this exact JSON format:
{
  "estimate": <number of minutes per week>,
  "reasoning": "<2-3 sentence explanation of how this module saves time and what tasks it replaces or accelerates>"
}`;

    const response = await supabase.functions.invoke('ai-complete', {
      body: {
        provider: 'claude',
        model: 'claude-sonnet-4-20250514',
        prompt,
        systemPrompt: 'You are a productivity analyst who estimates time savings for AI tools and automations. Always respond with valid JSON only, no additional text.',
        maxTokens: 300
      }
    });

    if (response.error) {
      console.error('AI analysis error:', response.error);
      // Fall back to rule-based calculation
      const fallbackEstimate = analyzeModuleTimeSaved(moduleType, tags, title, description, setupInstructions);
      return {
        estimate: fallbackEstimate,
        reasoning: 'Time estimate based on module type and content analysis.'
      };
    }

    const aiResponse = response.data;
    
    // Parse the AI response
    try {
      // Extract JSON from the response content
      const content = aiResponse.content || '';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        const estimate = Math.min(Math.max(parseInt(parsed.estimate) || 30, 15), 300); // Clamp between 15-300 min
        const reasoning = parsed.reasoning || 'This module helps streamline your workflow.';
        
        return { estimate, reasoning };
      }
    } catch (parseError) {
      console.error('Error parsing AI response:', parseError);
    }

    // Fall back to rule-based calculation
    const fallbackEstimate = analyzeModuleTimeSaved(moduleType, tags, title, description, setupInstructions);
    return {
      estimate: fallbackEstimate,
      reasoning: 'Time estimate based on module type and content analysis.'
    };

  } catch (error) {
    console.error('AI time savings analysis failed:', error);
    // Fall back to rule-based calculation
    const fallbackEstimate = analyzeModuleTimeSaved(moduleType, tags, title, description, setupInstructions);
    return {
      estimate: fallbackEstimate,
      reasoning: 'Time estimate based on module type and content analysis.'
    };
  }
}

/**
 * Get recommended modules based on a module's tags, type, and content
 */
export async function getRecommendedModules(
  moduleId: string,
  moduleType: string,
  tags: string[],
  limit: number = 4
): Promise<Array<{ id: string; title: string; description: string; module_type: string; tags: string[]; created_by: string; likes: number }>> {
  try {
    // Get modules - without likes column since it may not exist
    const { data: allModules, error } = await supabase
      .from('modules')
      .select('id, title, description, module_type, tags, created_by')
      .neq('id', moduleId)
      .limit(50);

    if (error) {
      console.error('Error fetching recommended modules:', error);
      return [];
    }
    
    if (!allModules || allModules.length === 0) {
      return [];
    }

    // Score each module based on relevance
    const scoredModules = allModules.map(m => {
      let score = 0;
      
      // Same type = high score
      if (m.module_type && moduleType && m.module_type === moduleType) score += 30;
      
      // Matching tags = points per match
      const moduleTags = (m.tags || []).map((t: string) => t.toLowerCase());
      const sourceTags = (tags || []).map(t => t.toLowerCase());
      
      sourceTags.forEach(tag => {
        if (moduleTags.includes(tag)) score += 15;
        // Partial match
        if (moduleTags.some((mt: string) => mt.includes(tag) || tag.includes(mt))) score += 5;
      });
      
      // Minimum score for having any content
      if (m.title && m.description) score += 5;
      
      return { ...m, likes: 0, score };
    });

    // Sort by score and return top matches (lower threshold)
    const results = scoredModules
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .filter(m => m.score > 0);
    
    return results;
  } catch (err) {
    console.error('Error in getRecommendedModules:', err);
    return [];
  }
}

/**
 * Get personalized module recommendations for a user based on their activity
 */
export async function getUserRecommendedModules(
  userId: string,
  limit: number = 6
): Promise<Array<{ id: string; title: string; description: string; module_type: string; tags: string[]; created_by: string; likes: number; reason: string }>> {
  try {
    // Get user's recent activity to understand preferences
    let recentActivity: Array<{ resource_id: string; event_type: string; metadata: Record<string, unknown> | null }> | null = null;
    
    try {
      const { data, error } = await supabase
        .from('analytics_events')
        .select('resource_id, event_type, metadata')
        .eq('user_id', userId)
        .in('event_type', ['module_view', 'module_copy', 'module_implement', 'module_like'])
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (!error) recentActivity = data;
    } catch (e) {
      // analytics_events table may not exist
    }

    // Get modules the user created
    let userModules: Array<{ module_type: string; tags: string[] }> | null = null;
    
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('module_type, tags')
        .eq('created_by_id', userId)
        .limit(10);
      
      if (!error) userModules = data;
    } catch (e) {
      // Error fetching user modules
    }

    // Build preference profile from activity
    const engagedModuleIds = new Set((recentActivity || []).map(a => a.resource_id).filter(Boolean));
    
    // Collect preferred types and tags
    const typePreferences: Record<string, number> = {};
    const tagPreferences: Record<string, number> = {};

    // From activity
    if (recentActivity) {
      for (const activity of recentActivity) {
        if (activity.metadata) {
          const meta = activity.metadata as Record<string, unknown>;
          if (meta.module_type) {
            const type = meta.module_type as string;
            typePreferences[type] = (typePreferences[type] || 0) + 2;
          }
          if (meta.tags && Array.isArray(meta.tags)) {
            (meta.tags as string[]).forEach(tag => {
              tagPreferences[tag.toLowerCase()] = (tagPreferences[tag.toLowerCase()] || 0) + 1;
            });
          }
        }
      }
    }

    // From created modules (user interests)
    (userModules || []).forEach(m => {
      if (m.module_type) {
        typePreferences[m.module_type] = (typePreferences[m.module_type] || 0) + 3;
      }
      (m.tags || []).forEach((tag: string) => {
        tagPreferences[tag.toLowerCase()] = (tagPreferences[tag.toLowerCase()] || 0) + 2;
      });
    });

    // Get all modules - try with status filter first, then without
    // Get all modules - without likes column since it doesn't exist
    const { data: allModules, error: modulesError } = await supabase
      .from('modules')
      .select('id, title, description, module_type, tags, created_by')
      .limit(100);

    if (modulesError) {
      console.error('Error fetching modules for recommendations:', modulesError);
      return [];
    }

    if (!allModules || allModules.length === 0) {
      return [];
    }

    // Score modules based on user preferences
    const scoredModules = allModules
      .filter(m => !engagedModuleIds.has(m.id))
      .map(m => {
        let score = 0;
        let reason = '';

        // Type preference match
        if (m.module_type && typePreferences[m.module_type]) {
          score += typePreferences[m.module_type] * 10;
          reason = `Based on your interest in ${m.module_type.replace(/_/g, ' ')}s`;
        }

        // Tag preference match
        const moduleTags = (m.tags || []).map((t: string) => t.toLowerCase());
        let matchedTags: string[] = [];
        moduleTags.forEach((tag: string) => {
          if (tagPreferences[tag]) {
            score += tagPreferences[tag] * 5;
            matchedTags.push(tag);
          }
        });
        
        if (matchedTags.length > 0 && !reason) {
          reason = `Related to: ${matchedTags.slice(0, 2).join(', ')}`;
        }

        // Give all modules a base score so we get some results
        score += 5;

        // If no specific reason, mark as new
        if (!reason) {
          reason = 'Recently added';
        }

        return { ...m, likes: 0, score, reason };
      });

    // Sort and return top recommendations
    const results = scoredModules
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);
    
    return results;
  } catch (err) {
    console.error('Error in getUserRecommendedModules:', err);
    return [];
  }
}

export interface AnalyticsEvent {
  event_type: string;
  resource_type?: string;
  resource_id?: string;
  resource_title?: string;
  metadata?: Record<string, unknown>;
}

export interface AnalyticsSummary {
  totalEvents: number;
  totalMinutesSaved: number;
  totalHoursSaved: number;
  totalDollarsSaved: number;
  totalImplementations: number;
  avgMinutesPerImplementation: number;
  eventsByType: Record<string, { count: number; minutesSaved: number }>;
  topResources: Array<{ title: string; type: string; views: number; minutesSaved: number }>;
  topUsers: Array<{ name: string; events: number; minutesSaved: number }>;
  dailyTrend: Array<{ date: string; events: number; minutesSaved: number }>;
}

/**
 * Track an analytics event
 */
export async function trackEvent(event: AnalyticsEvent): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Use custom time saved if provided (for module implementations), otherwise use default
    let timeSaved = TIME_SAVED_MINUTES[event.event_type] || 0;
    if (event.metadata?.custom_time_saved && typeof event.metadata.custom_time_saved === 'number') {
      timeSaved = event.metadata.custom_time_saved;
    }

    await supabase.from('analytics_events').insert({
      user_id: user.id,
      event_type: event.event_type,
      resource_type: event.resource_type,
      resource_id: event.resource_id,
      resource_title: event.resource_title,
      metadata: event.metadata || {},
      time_saved_minutes: timeSaved,
    });
  } catch (error) {
    // Silently fail - analytics shouldn't break the app
    console.error('Analytics tracking error:', error);
  }
}

/**
 * Track module view
 */
export function trackModuleView(moduleId: string, moduleTitle: string): void {
  trackEvent({
    event_type: 'module_view',
    resource_type: 'module',
    resource_id: moduleId,
    resource_title: moduleTitle,
  });
}

/**
 * Track module copy (when user copies content)
 */
export function trackModuleCopy(moduleId: string, moduleTitle: string): void {
  trackEvent({
    event_type: 'module_copy',
    resource_type: 'module',
    resource_id: moduleId,
    resource_title: moduleTitle,
  });
}

/**
 * Track module like
 */
export function trackModuleLike(moduleId: string, moduleTitle: string): void {
  trackEvent({
    event_type: 'module_like',
    resource_type: 'module',
    resource_id: moduleId,
    resource_title: moduleTitle,
  });
}

/**
 * Track module creation
 */
export function trackModuleCreate(moduleId: string, moduleTitle: string): void {
  trackEvent({
    event_type: 'module_create',
    resource_type: 'module',
    resource_id: moduleId,
    resource_title: moduleTitle,
  });
}

/**
 * Track how-to view
 */
export function trackHowToView(howtoId: string, howtoTitle: string): void {
  trackEvent({
    event_type: 'howto_view',
    resource_type: 'howto',
    resource_id: howtoId,
    resource_title: howtoTitle,
  });
}

/**
 * Track how-to creation
 */
export function trackHowToCreate(howtoId: string, howtoTitle: string): void {
  trackEvent({
    event_type: 'howto_create',
    resource_type: 'howto',
    resource_id: howtoId,
    resource_title: howtoTitle,
  });
}

/**
 * Track how-to copy (when user copies content)
 */
export function trackHowToCopy(howtoId: string, howtoTitle: string): void {
  trackEvent({
    event_type: 'howto_copy',
    resource_type: 'howto',
    resource_id: howtoId,
    resource_title: howtoTitle,
  });
}

/**
 * Track agent run
 */
export function trackAgentRun(agentId: string, agentName: string): void {
  trackEvent({
    event_type: 'agent_run',
    resource_type: 'agent',
    resource_id: agentId,
    resource_title: agentName,
  });
}

/**
 * Track agent creation
 */
export function trackAgentCreate(agentId: string, agentName: string): void {
  trackEvent({
    event_type: 'agent_create',
    resource_type: 'agent',
    resource_id: agentId,
    resource_title: agentName,
  });
}

/**
 * Track search
 */
export function trackSearch(query: string): void {
  trackEvent({
    event_type: 'search',
    metadata: { query },
  });
}

/**
 * Track module implementation
 */
export function trackModuleImplementation(moduleId: string, moduleTitle: string, timeSaved: number): void {
  trackEvent({
    event_type: 'module_implement',
    resource_type: 'module',
    resource_id: moduleId,
    resource_title: moduleTitle,
    metadata: { custom_time_saved: timeSaved },
  });
}

/**
 * Mark a module as implemented by the current user
 */
export async function implementModule(moduleId: string, notes?: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not logged in' };

    // Get module details including tags for time calculation
    const { data: module, error: moduleError } = await supabase
      .from('modules')
      .select('title, created_by_id, module_type, tags')
      .eq('id', moduleId)
      .single();

    if (moduleError) {
      console.error('Error fetching module:', moduleError);
      return { success: false, error: 'Module not found' };
    }

    // Calculate time saved based on module type AND tags
    const timeSaved = calculateModuleTimeSaved(module?.module_type || '', module?.tags || []);

    // Try to insert into module_implementations table (may not exist)
    try {
      // Check if already implemented
      const { data: existing } = await supabase
        .from('module_implementations')
        .select('id')
        .eq('module_id', moduleId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing) {
        return { success: false, error: 'Already implemented' };
      }

      // Insert implementation record
      await supabase
        .from('module_implementations')
        .insert({
          module_id: moduleId,
          user_id: user.id,
          notes: notes || null,
        });
    } catch (tableError: unknown) {
      // Table might not exist - log but continue to track the event
      console.warn('module_implementations table may not exist, continuing with analytics tracking:', tableError);
    }

    // Track the implementation event
    await trackModuleImplementation(moduleId, module?.title || 'Unknown', timeSaved);

    return { success: true };
  } catch (error) {
    console.error('Error implementing module:', error);
    return { success: false, error: (error as Error).message };
  }
}

/**
 * Check if user has implemented a module
 */
export async function hasImplementedModule(moduleId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data } = await supabase
      .from('module_implementations')
      .select('id')
      .eq('module_id', moduleId)
      .eq('user_id', user.id)
      .single();

    return !!data;
  } catch {
    return false;
  }
}

/**
 * Get implementation count for a module
 */
export async function getModuleImplementationCount(moduleId: string): Promise<number> {
  try {
    const { count } = await supabase
      .from('module_implementations')
      .select('*', { count: 'exact', head: true })
      .eq('module_id', moduleId);

    return count || 0;
  } catch {
    return 0;
  }
}

/**
 * Get user-specific analytics for profile page
 */
export async function getUserAnalytics(userId?: string): Promise<{
  totalTimeSaved: number;
  totalDollarsSaved: number;
  modulesImplemented: number;
  modulesCreated: number;
  contributions: number;
  eventsByType: Record<string, number>;
  recentActivity: Array<{ type: string; title: string; timeSaved: number; date: string }>;
} | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const targetUserId = userId || user?.id;
    if (!targetUserId) return null;

    // Get user's events
    const { data: events, error } = await supabase
      .from('analytics_events')
      .select('*')
      .eq('user_id', targetUserId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Get modules created by user
    const { count: modulesCreated } = await supabase
      .from('modules')
      .select('*', { count: 'exact', head: true })
      .eq('created_by_id', targetUserId);

    // Get implementations count
    const { count: modulesImplemented } = await supabase
      .from('module_implementations')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', targetUserId);

    const totalTimeSaved = (events || []).reduce((sum, e) => sum + (e.time_saved_minutes || 0), 0);
    
    // Group by event type
    const eventsByType: Record<string, number> = {};
    (events || []).forEach(e => {
      eventsByType[e.event_type] = (eventsByType[e.event_type] || 0) + 1;
    });

    // Recent activity (last 20)
    const recentActivity = (events || []).slice(0, 20).map(e => ({
      type: e.event_type,
      title: e.resource_title || 'Unknown',
      timeSaved: e.time_saved_minutes || 0,
      date: e.created_at,
    }));

    return {
      totalTimeSaved,
      totalDollarsSaved: (totalTimeSaved / 60) * HOURLY_RATE,
      modulesImplemented: modulesImplemented || 0,
      modulesCreated: modulesCreated || 0,
      contributions: (modulesCreated || 0) + (modulesImplemented || 0),
      eventsByType,
      recentActivity,
    };
  } catch (error) {
    console.error('Error fetching user analytics:', error);
    return null;
  }
}

/**
 * Get top module creators by impact (time saved by others using their modules)
 */
export async function getTopCreatorsByImpact(): Promise<Array<{
  userId: string;
  name: string;
  modulesCreated: number;
  totalImpact: number; // minutes saved by others
  implementations: number;
}>> {
  try {
    // Get all modules with their creators - include created_by (name string)
    const { data: modules, error: modulesError } = await supabase
      .from('modules')
      .select('id, title, created_by, created_by_id, module_type');

    if (modulesError) {
      console.error('Error fetching modules for creators:', modulesError);
      return [];
    }

    if (!modules || modules.length === 0) return [];

    // Get all view/copy/implement events for modules from analytics
    const { data: events } = await supabase
      .from('analytics_events')
      .select('resource_id, time_saved_minutes, event_type')
      .eq('resource_type', 'module');

    // Calculate impact per creator - use created_by (name) as the key since it's more reliable
    const creatorImpact: Record<string, { name: string; modulesCreated: number; totalImpact: number; implementations: number }> = {};

    modules.forEach(m => {
      // Use created_by (name string) as the identifier
      const creatorKey = m.created_by || m.created_by_id || 'Unknown';
      if (!creatorKey || creatorKey === 'Unknown') return;
      
      if (!creatorImpact[creatorKey]) {
        creatorImpact[creatorKey] = { 
          name: m.created_by || 'Team Member',
          modulesCreated: 0, 
          totalImpact: 0, 
          implementations: 0 
        };
      }
      creatorImpact[creatorKey].modulesCreated++;
    });

    // Add impact from analytics events
    (events || []).forEach(e => {
      if (!e.resource_id) return;
      const module = modules.find(m => m.id === e.resource_id);
      if (!module) return;
      
      const creatorKey = module.created_by || module.created_by_id || 'Unknown';
      if (creatorImpact[creatorKey]) {
        creatorImpact[creatorKey].totalImpact += e.time_saved_minutes || 0;
        if (e.event_type === 'module_implement') {
          creatorImpact[creatorKey].implementations++;
        }
      }
    });

    return Object.entries(creatorImpact)
      .filter(([key, data]) => data.modulesCreated > 0 && key !== 'Unknown')
      .map(([userId, data]) => ({
        userId,
        name: data.name,
        modulesCreated: data.modulesCreated,
        totalImpact: data.totalImpact,
        implementations: data.implementations,
      }))
      .sort((a, b) => {
        // Sort by total impact, then by modules created
        if (b.totalImpact !== a.totalImpact) return b.totalImpact - a.totalImpact;
        return b.modulesCreated - a.modulesCreated;
      })
      .slice(0, 10);
  } catch (error) {
    console.error('Error fetching top creators:', error);
    return [];
  }
}

/**
 * Get analytics summary for admin dashboard
 */
export async function getAnalyticsSummary(
  startDate?: Date,
  endDate?: Date
): Promise<AnalyticsSummary | null> {
  try {
    let query = supabase
      .from('analytics_events')
      .select('*');

    if (startDate) {
      query = query.gte('created_at', startDate.toISOString());
    }
    if (endDate) {
      query = query.lte('created_at', endDate.toISOString());
    }

    const { data: events, error } = await query;

    if (error) throw error;
    if (!events || events.length === 0) {
      return {
        totalEvents: 0,
        totalMinutesSaved: 0,
        totalHoursSaved: 0,
        totalDollarsSaved: 0,
        totalImplementations: 0,
        avgMinutesPerImplementation: 0,
        eventsByType: {},
        topResources: [],
        topUsers: [],
        dailyTrend: [],
      };
    }

    // Calculate totals from analytics events
    const totalMinutesSaved = events.reduce((sum, e) => sum + (e.time_saved_minutes || 0), 0);
    const totalHoursSaved = totalMinutesSaved / 60;
    const totalDollarsSaved = totalHoursSaved * HOURLY_RATE;
    
    // Get actual module implementations from module_implementations table
    // Query separately to avoid join issues, then calculate time saved
    let totalImplementations = 0;
    let implementationTimeSaved = 0;
    let avgMinutesPerImplementation = 0;
    
    try {
      // First, get all implementations (simple query, no join)
      const { data: implementations } = await supabase
        .from('module_implementations')
        .select('module_id');
      
      if (implementations && implementations.length > 0) {
        totalImplementations = implementations.length;
        
        // Get the module IDs that were implemented
        const moduleIds = implementations.map(impl => impl.module_id).filter(Boolean);
        
        if (moduleIds.length > 0) {
          // Fetch module details separately
          const { data: modules } = await supabase
            .from('modules')
            .select('id, module_type, tags, time_saved_estimate')
            .in('id', moduleIds);
          
          if (modules && modules.length > 0) {
            // Create a map for quick lookup
            const moduleMap = new Map(modules.map(m => [m.id, m]));
            
            // Calculate time saved for each implementation
            implementationTimeSaved = implementations.reduce((sum, impl) => {
              const moduleData = moduleMap.get(impl.module_id);
              if (moduleData) {
                // Use time_saved_estimate if available, otherwise calculate from type/tags
                const timeSaved = moduleData.time_saved_estimate || 
                  calculateModuleTimeSaved(moduleData.module_type || '', moduleData.tags || []);
                return sum + timeSaved;
              }
              // Default if module not found
              return sum + 60;
            }, 0);
          } else {
            // Modules not found, use default estimate
            implementationTimeSaved = totalImplementations * 60;
          }
        }
        
        avgMinutesPerImplementation = totalImplementations > 0 
          ? implementationTimeSaved / totalImplementations 
          : 0;
      }
    } catch (e) {
      // If module_implementations table doesn't exist, fall back to analytics events
      const implementationEvents = events.filter(e => e.event_type === 'module_implement');
      totalImplementations = implementationEvents.length;
      if (totalImplementations > 0) {
        // Use calculated time from analytics or default
        implementationTimeSaved = implementationEvents.reduce((sum, e) => 
          sum + (e.time_saved_minutes || 60), 0);
        avgMinutesPerImplementation = implementationTimeSaved / totalImplementations;
      }
    }

    // Group by event type
    const eventsByType: Record<string, { count: number; minutesSaved: number }> = {};
    events.forEach(e => {
      if (!eventsByType[e.event_type]) {
        eventsByType[e.event_type] = { count: 0, minutesSaved: 0 };
      }
      eventsByType[e.event_type].count++;
      eventsByType[e.event_type].minutesSaved += e.time_saved_minutes || 0;
    });

    // Top resources
    const resourceMap: Record<string, { title: string; type: string; views: number; minutesSaved: number }> = {};
    events.forEach(e => {
      if (e.resource_id && e.resource_title) {
        if (!resourceMap[e.resource_id]) {
          resourceMap[e.resource_id] = {
            title: e.resource_title,
            type: e.resource_type || 'unknown',
            views: 0,
            minutesSaved: 0,
          };
        }
        resourceMap[e.resource_id].views++;
        resourceMap[e.resource_id].minutesSaved += e.time_saved_minutes || 0;
      }
    });
    const topResources = Object.values(resourceMap)
      .sort((a, b) => b.minutesSaved - a.minutesSaved)
      .slice(0, 10);

    // Top users
    const userMap: Record<string, { name: string; email: string; events: number; minutesSaved: number }> = {};
    events.forEach(e => {
      if (e.user_id) {
        if (!userMap[e.user_id]) {
          userMap[e.user_id] = {
            name: e.user_id, // Will be replaced with actual name
            email: '',
            events: 0,
            minutesSaved: 0,
          };
        }
        userMap[e.user_id].events++;
        userMap[e.user_id].minutesSaved += e.time_saved_minutes || 0;
      }
    });

    // Get user names - first try user_profiles, then try to get from modules created_by
    const userIds = Object.keys(userMap);
    if (userIds.length > 0) {
      // Fetch all profiles
      const { data: allProfiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, email');
      
      // Build a map of profile id -> display name
      const profileMap: Record<string, { name: string; email: string }> = {};
      (allProfiles || []).forEach(p => {
        profileMap[p.id] = {
          name: p.full_name || p.email || '',
          email: p.email || ''
        };
      });

      // Match users to profiles
      userIds.forEach(uid => {
        if (profileMap[uid] && profileMap[uid].name) {
          userMap[uid].name = profileMap[uid].name;
          userMap[uid].email = profileMap[uid].email;
        }
      });

      // For users still not found, try to get name from modules they created
      const missingUsers = Object.keys(userMap).filter(uid => !userMap[uid].name || userMap[uid].name === uid);
      if (missingUsers.length > 0) {
        const { data: userModules } = await supabase
          .from('modules')
          .select('created_by, created_by_id')
          .in('created_by_id', missingUsers);
        
        (userModules || []).forEach(m => {
          if (m.created_by_id && m.created_by && userMap[m.created_by_id]) {
            userMap[m.created_by_id].name = m.created_by;
          }
        });
      }

      // Final fallback to "Team Member" for any still unresolved
      Object.keys(userMap).forEach(uid => {
        if (!userMap[uid].name || userMap[uid].name === uid) {
          userMap[uid].name = 'Team Member';
        }
      });
    }

    const topUsers = Object.values(userMap)
      .sort((a, b) => b.minutesSaved - a.minutesSaved)
      .slice(0, 10);

    // Daily trend (last 30 days)
    const dailyMap: Record<string, { events: number; minutesSaved: number }> = {};
    events.forEach(e => {
      const date = new Date(e.created_at).toISOString().split('T')[0];
      if (!dailyMap[date]) {
        dailyMap[date] = { events: 0, minutesSaved: 0 };
      }
      dailyMap[date].events++;
      dailyMap[date].minutesSaved += e.time_saved_minutes || 0;
    });
    const dailyTrend = Object.entries(dailyMap)
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-30);

    return {
      totalEvents: events.length,
      totalMinutesSaved,
      totalHoursSaved,
      totalDollarsSaved,
      totalImplementations,
      avgMinutesPerImplementation,
      eventsByType,
      topResources,
      topUsers,
      dailyTrend,
    };
  } catch (error) {
    console.error('Error fetching analytics summary:', error);
    return null;
  }
}
